<template>
  <div class="my-10">
    <!-- ✅ POPUP only for Publications HOME -->
    <PopupWrapper :platform="100" />

    <!-- NEWS SLIDER -->
    <div v-if="HABARLAR_ENABLED && news && news.length">
      <Splide
        class="mt-8 max-md:mt-0"
        :has-track="false"
        :options="mediaNewsOptions"
        :extensions="{ autoscrollExt }"
        aria-label="carouselBanner"
      >
        <SplideTrack class="rounded-lg py-2">
          <SplideSlide v-for="data in news" :key="data.id">
            <router-link :to="{ name: 'newsDetail', params: { id: data?.id } }">
              <NewsTile class="w-[25.8125rem]" :show-category="true" :detail="data" />
            </router-link>
          </SplideSlide>
        </SplideTrack>
      </Splide>
    </div>

    <!-- BIG BANNERS -->
    <CarouselBanner
      v-if="publicationBanners && publicationBanners.length"
      :content="publicationBanners"
    />

    <!-- SMALL POSTERS -->
    <PosterBannerSmall
      v-if="publicationSmallPosters && publicationSmallPosters.length"
      :content="publicationSmallPosters"
    />

    <!-- POSTERS 2x -->
    <div v-if="poster1.length || poster2.length" class="mt-2 grid grid-cols-2 gap-2">
      <PosterBanner v-if="poster1.length" :content="poster1" />
      <PosterBanner v-if="poster2.length" :content="poster2" />
    </div>

    <!-- VIDEOS -->
    <div
      v-if="videos1.length || videos2.length"
      class="mt-2 grid grid-cols-2 gap-2 max-md:grid-cols-1"
    >
      <VideoBanner v-if="videos1.length" :content="videos1" />
      <VideoBanner v-if="videos2.length" :content="videos2" />
    </div>

    <!-- PUBLICATIONS -->
    <div v-if="publications.length" class="my-20">
      <div class="flex justify-between">
        <h2 class="text-white text-4xl font-bold bg-main-color py-0.5 px-4">
          {{ $t('nav.publications') }}
        </h2>

        <router-link
          :to="{ name: 'publications' }"
          class="text-sm bg-main-color py-3 px-6 border border-solid rounded text-white hover:bg-accent-color"
        >
          {{ $t('page.viewAll') }}
        </router-link>
      </div>

      <div
        class="mt-5 grid grid-cols-4 gap-8 max-lg:grid-cols-3 max-lg:gap-5 max-md:grid-cols-2 max-md:gap-4"
      >
        <router-link
          v-for="item in publications"
          :key="item.id"
          :to="{ name: 'publicationDetail', params: { id: item.id } }"
        >
          <PublicationCard :has_name="true" :detail="item" />
        </router-link>
      </div>
    </div>

    <!-- SELECTEDS -->
    <div v-if="selecteds.length" class="my-20">
      <h2 class="text-white text-4xl w-max font-bold bg-main-color py-0.5 px-4">
        {{ $t('page.selecteds') }}
      </h2>

      <!-- MALL -->
      <div
        v-if="selecteds.filter(i => i.content_type === 'mall_store').length"
        class="mt-5 grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2"
      >
        <router-link
          v-for="item in selecteds.filter(i => i.content_type === 'mall_store')"
          :key="item.id"
          :to="{ name: 'storeDetail', params: { id: item.id } }"
        >
          <StoreCard :detail="item" />
        </router-link>
      </div>

      <!-- FOOD -->
      <div
        v-if="selecteds.filter(i => i.content_type === 'exhibition_store').length"
        class="mt-5 grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2"
      >
        <router-link
          v-for="item in selecteds.filter(i => i.content_type === 'exhibition_store')"
          :key="item.id"
          :to="{ name: 'foodRestaurantDetail', params: { id: item.id } }"
        >
          <StoreCard :detail="item" />
        </router-link>
      </div>

      <!-- BUSINESS -->
      <div
        v-if="selecteds.filter(i => i.content_type === 'business_account').length"
        class="mt-5 grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2"
      >
        <router-link
          v-for="item in selecteds.filter(i => i.content_type === 'business_account')"
          :key="item.id"
          :to="{ name: 'businessAccountsDetail', params: { id: item.id } }"
        >
          <BusinessAccountCard :detail="item" />
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

import NewsTile from '@cmp/media/NewsTile.vue';
import StoreCard from '@cmp/mall/StoreCard.vue';
import BusinessAccountCard from '@cmp/media/BusinessAccountCard.vue';
import PublicationCard from '@cmp/publication/PublicationCard.vue';
import CarouselBanner from '@cmp/CarouselBanner.vue';
import PosterBannerSmall from '@cmp/PosterBannerSmall.vue';
import PosterBanner from '@cmp/PosterBanner.vue';
import VideoBanner from '@cmp/VideoBanner.vue';
import api from '@/api';
import PopupWrapper from '@/components/PopupWrapper.vue';
import { FEATURE_FLAGS } from '@/constants';

export default {
  name: 'PublicationHome',
  components: {
    PopupWrapper,
    PublicationCard,
    NewsTile,
    CarouselBanner,
    PosterBannerSmall,
    PosterBanner,
    VideoBanner,
    StoreCard,
    BusinessAccountCard,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  data() {
    return {
      HABARLAR_ENABLED: FEATURE_FLAGS.HABARLAR,
      _reqId: 0,
      page: 1,
      loading: false,
      publications: [],
      selecteds: [],
      publicationBanners: [],
      publicationSmallPosters: [],
      news: [],
      videos1: [],
      videos2: [],
      poster1: [],
      poster2: [],

      mediaNewsOptions: {
        type: 'loop',
        autoWidth: true,
        gap: '20px',
        autoScroll: { speed: 1 },
        pagination: false,
        arrows: false,
        rewind: true,
      },
    };
  },

  computed: {
    autoscrollExt() {
      return AutoScroll;
    },
    locationId() {
      return this.$store?.selectedLocation?.id;
    },
  },

  methods: {
    async getPublications() {
      try {
        const response = await api.get('publications/publications/', {
          params: {
            page: this.page,
            add_home: true,
            home_location: this.locationId,
          },
        });

        // Проверка на пустые данные
        if (!response.data || !response.data.results) {
          console.warn('No publications data found');
          this.$toast.info('No publications available at the moment.');
          return;
        }

        this.publications.push(...response.data.results);
        this.page += 1;
      } catch (error) {
        console.error('Error loading publications:', error);
        this.$toast.error('Failed to load publications. Please try again later.');
      }
    },

    async getNews() {
      try {
        const r = await api.get('posts/announcements/', {
          params: {
            page: 1,
            add_home: true,
            location: this.locationId,
          },
        });

        // Проверка на пустые данные
        if (!r.data || !r.data.results) {
          console.warn('No news data found');
          this.$toast.info('No news available at the moment.');
          return;
        }

        this.news = r.data.results || [];
      } catch (error) {
        console.error('Error loading news:', error);
        this.$toast.error('Failed to load news. Please try again later.');
      }
    },

    async getVideos() {
      try {
        const response = await api.get('posts/home-videos/', {
          params: {
            page: 1,
            location: this.locationId,
          },
        });

        const raw = Array.isArray(response.data) ? response.data : (response.data?.results ?? []);

        // Проверка на пустые данные
        if (!raw.length) {
          console.warn('No videos found');
          this.$toast.info('No videos available at the moment.');
          return;
        }

        const data = raw
          .map(item => {
            const v = item?.video;
            return {
              id: v?.id,
              thumbnail: v?.thumbnail,
              video_url: v?.video,
              home_video_id: item?.id,
            };
          })
          .filter(v => v.id && v.thumbnail);

        const half = Math.ceil(data.length / 2);
        this.videos1 = data.slice(0, half);
        this.videos2 = data.slice(half);
      } catch (error) {
        console.error('Error loading videos:', error);
        this.$toast.error('Failed to load videos. Please try again later.');
      }
    },

    async getPublicationBanner() {
      const r = await api.get('commercials/advertisements/', {
        params: {
          type: 201,
          location: this.locationId,
        },
      });
      this.publicationBanners = r.data || [];
    },

    async getPublicationPoster() {
      const r = await api.get('commercials/advertisements/', {
        params: {
          type: 202,
          location: this.locationId,
        },
      });

      const data = r.data || [];
      this.poster1 = data.filter(el => el.group_number === 1);
      this.poster2 = data.filter(el => el.group_number === 2);
    },

    async getPublicationSmallPoster() {
      const r = await api.get('commercials/advertisements/', {
        params: {
          type: 203,
          location: this.locationId,
        },
      });
      this.publicationSmallPosters = r.data || [];
    },

    async getCatalog(locationId) {
      try {
        const r = await api.get(`posts/catalogs/${locationId}/`);
        const hasCatalog = !!r.data?.has_catalog;

        this.$store.catalog = {
          ...(this.$store.catalog || {}),
          has_catalog: hasCatalog,
          file: r.data?.file ?? null,
          download_count: r.data?.download_count ?? 0,
          isNew: hasCatalog,
        };
      } catch (e) {
        // если ошибка — оставляем сброшенное состояние (точка погашена)
        this.$store.catalog = {
          ...(this.$store.catalog || {}),
          has_catalog: false,
          isNew: false,
          file: null,
          download_count: 0,
        };
      }
    },

    async getMixobjects() {
      const r = await api.get('publications/mixed_objects/', {
        params: {
          add_home: true,
          location: this.locationId,
        },
      });
      this.selecteds = r.data || [];
    },
  },

  watch: {
    locationId: {
      immediate: true,
      async handler() {
        if (!this.locationId) return;

        const reqId = ++this._reqId; // важно

        this.loading = true;

        try {
          // 🔥 СБРОС ДАННЫХ
          this.publicationBanners = [];
          this.publicationSmallPosters = [];
          this.poster1 = [];
          this.poster2 = [];
          this.videos1 = [];
          this.videos2 = [];
          this.publications = [];
          this.news = [];
          this.selecteds = [];
          this.page = 1;
          this.$store.catalog = {
            ...(this.$store.catalog || {}),
            has_catalog: false,
            isNew: false,
            file: null,
            download_count: 0,
          };

          // 🔄 ЗАГРУЗКА
          await this.getPublicationBanner();
          await this.getPublicationPoster();
          await this.getPublicationSmallPoster();
          await this.getPublications();
          if (this.HABARLAR_ENABLED) await this.getNews();
          await this.getVideos();
          await this.getMixobjects();
          await this.getCatalog(this.locationId);

          // ⛔ если локация сменилась во время загрузки — игнорируем результат
          if (reqId !== this._reqId) return;
        } catch (error) {
          console.error('Error loading data:', error);
          this.$toast?.error?.('Failed to load data. Please try again later.');
        } finally {
          // ✅ loading снимаем только для актуального запроса
          if (reqId === this._reqId) {
            this.loading = false;
          }
        }
      }, // handler
    }, // locationId
  }, // watch
};
</script>
